import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { PageTitle } from "@/components/site/ReleaseCard";
import { SocialLinks } from "@/components/site/Social";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { name: "", email: "", subject: "Invio demo", demo_link: "", message: "", website: "" };
const field = "bg-[#12141c] border-white/10 h-12 focus-visible:ring-emerald-500";

export default function Contact() {
  const { settings } = useSettings();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Messaggio inviato! Ti risponderemo presto.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatError(err));
    } finally { setSending(false); }
  };

  return (
    <div data-testid="contact-page">
      <PageTitle over="Contatti" title="Parliamo" sub="Hai una demo, una proposta o vuoi collaborare? Scrivici." />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.4fr_1fr] gap-14">
        <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
          <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
          <div className="grid sm:grid-cols-2 gap-4">
            <Input required placeholder="Nome / Nome artista" value={form.name} onChange={set("name")} className={field} data-testid="contact-name-input" />
            <Input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={field} data-testid="contact-email-input" />
          </div>
          <select value={form.subject} onChange={set("subject")} className={`${field} w-full rounded-md border px-3 text-sm`} data-testid="contact-subject-select">
            <option>Invio demo</option><option>Collaborazione</option><option>Stampa / Media</option><option>Altro</option>
          </select>
          <Input placeholder="Link demo (SoundCloud, Dropbox…) — opzionale" value={form.demo_link} onChange={set("demo_link")} className={field} data-testid="contact-demo-input" />
          <Textarea required placeholder="Messaggio" rows={6} value={form.message} onChange={set("message")} className="bg-[#12141c] border-white/10 focus-visible:ring-emerald-500" data-testid="contact-message-input" />
          <button disabled={sending} data-testid="contact-submit-btn" className="inline-flex items-center gap-2 rounded-full bg-emerald-500 text-black px-8 py-3.5 font-semibold hover:bg-emerald-400 disabled:opacity-60 transition-colors">
            {sending ? "Invio…" : "Invia messaggio"} <Send className="h-4 w-4" />
          </button>
        </form>
        <aside className="space-y-8 border border-white/10 bg-[#0d0e12] p-8 h-fit">
          <div>
            <p className="font-mono-tga text-[10px] uppercase tracking-[0.3em] text-zinc-500">Sito</p>
            <p className="font-mono-tga text-sm text-emerald-400 mt-2">www.thegrooveanomalyrecords.com</p>
          </div>
          {settings.contact_email && (
            <div>
              <p className="font-mono-tga text-[10px] uppercase tracking-[0.3em] text-zinc-500">Email</p>
              <a href={`mailto:${settings.contact_email}`} className="text-sm mt-2 block hover:text-emerald-400" data-testid="contact-email-link">{settings.contact_email}</a>
            </div>
          )}
          {settings.city && (
            <div>
              <p className="font-mono-tga text-[10px] uppercase tracking-[0.3em] text-zinc-500">Base</p>
              <p className="text-sm mt-2">{settings.city}</p>
            </div>
          )}
          <div>
            <p className="font-mono-tga text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Social</p>
            <SocialLinks prefix="contact-social" />
          </div>
        </aside>
      </div>
    </div>
  );
}
